import { useEffect, useState } from "react";
import api from "@/lib/api";
import PostCard from "@/components/PostCard";
import CreatePost from "@/components/CreatePost";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import VerifiedBadge from "@/components/VerifiedBadge";
import ScholarAvatar from "@/components/ScholarAvatar";

export default function Feed() {
  const { user, meta, setFollowing } = useAuth();
  const [posts, setPosts] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [followPendingIds, setFollowPendingIds] = useState(new Set());
  const [events, setEvents] = useState([]);
  const [specialty, setSpecialty] = useState("All");

  const load = async () => {
    try {
      const params = specialty === "All" ? {} : { params: { specialty } };
      const url = specialty === "All" ? "/posts/feed" : "/posts";
      const { data } = await api.get(url, params);
      setPosts(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const loadSuggestions = async () => {
    try {
      const { data } = await api.get("/users/suggested");
      setSuggested(data);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [specialty]);

  useEffect(() => {
    if (!user?.id) return;
    loadSuggestions();
    api.get("/events").then(({ data }) => setEvents(data.slice(0, 3))).catch(() => {});
  }, [user?.id]);

  const follow = async (id) => {
    if (followPendingIds.has(id)) return;
    setFollowPendingIds((p) => new Set(p).add(id));
    // Optimistic remove
    setSuggested((s) => s.filter((u) => u.id !== id));
    try {
      await api.post(`/users/${id}/follow`);
      toast.success("Following");
      setFollowing(id, true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      // Rollback: re-fetch fresh suggestions
      loadSuggestions();
    } finally {
      setFollowPendingIds((p) => { const n = new Set(p); n.delete(id); return n; });
    }
  };

  return (
    <div data-testid="home-feed-container" className="grid lg:grid-cols-[240px_1fr_280px] gap-8">
      {/* Left sidebar */}
      <aside className="hidden lg:block">
        <div className="paper-card p-5 sticky top-24">
          <div className="label-eyebrow mb-3">Specialties</div>
          <div className="space-y-1">
            <button
              data-testid="specialty-filter-all"
              onClick={() => setSpecialty("All")}
              className={`w-full text-left px-3 py-1.5 rounded-md text-sm ${specialty === "All" ? "bg-[color:var(--ayur-emerald)] text-white" : "hover:bg-[color:var(--ayur-cream-2)]"}`}
            >
              My feed
            </button>
            {meta.specialties.map((s) => (
              <button
                key={s}
                data-testid={`specialty-filter-${s.toLowerCase().replace(/[^a-z]/g, "")}`}
                onClick={() => setSpecialty(s)}
                className={`w-full text-left px-3 py-1.5 rounded-md text-sm ${specialty === s ? "bg-[color:var(--ayur-emerald)] text-white" : "hover:bg-[color:var(--ayur-cream-2)] text-[color:var(--ayur-ink-2)]"}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </aside>

      {/* Feed */}
      <section className="space-y-5">
        <div className="paper-card p-4 flex items-center gap-3">
          <ScholarAvatar user={user} size={40} />
          <div className="flex-1"><CreatePost onCreated={load} /></div>
        </div>

        {specialty !== "All" && (
          <div className="flex items-center gap-2 text-sm">
            <span className="pill">{specialty}</span>
            <button onClick={() => setSpecialty("All")} className="text-[color:var(--ayur-terracotta)] link-underline">Clear filter</button>
          </div>
        )}

        {posts.length === 0 ? (
          <div className="paper-card p-12 text-center">
            <div className="label-eyebrow">Quiet here…</div>
            <h3 className="font-serif-display text-2xl mt-2 text-[color:var(--ayur-emerald)]">Your feed is waiting for voices</h3>
            <p className="text-[color:var(--ayur-ink-2)] mt-2 max-w-md mx-auto">
              You only see posts from scholars you follow. Head to <Link to="/app/discover" className="text-[color:var(--ayur-terracotta)] font-semibold link-underline">Discover</Link> or the <Link to="/app/gurus" className="text-[color:var(--ayur-terracotta)] font-semibold link-underline">Guru Directory</Link> and follow a few people to fill it up.
            </p>
          </div>
        ) : (
          posts.map((p) => <PostCard key={p.id} post={p} onChanged={load} />)
        )}
      </section>

      {/* Right sidebar */}
      <aside className="hidden lg:block">
        <div className="sticky top-24 space-y-6">
          <div className="paper-card p-5">
            <div className="label-eyebrow mb-3">Suggested scholars</div>
            <div className="space-y-3">
              {suggested.length === 0 && (
                <div className="text-sm text-[color:var(--ayur-mute)]">
                  No suggestions right now — you're either following everyone that matches your college / batch / specialty, or your profile is still light on details.
                </div>
              )}
              {suggested.slice(0, 5).map((u) => (
                <div key={u.id} className="flex items-center gap-3">
                  <ScholarAvatar user={u} size={36} />
                  <div className="flex-1 min-w-0">
                    <Link to={`/app/scholars/${u.id}`} className="text-sm font-semibold truncate block link-underline">
                      <span className="inline-flex items-center gap-1">
                        {u.full_name}
                        {u.verified && <VerifiedBadge size={12} />}
                      </span>
                    </Link>
                    <div className="text-xs text-[color:var(--ayur-mute)] truncate">
                      {u.shared_reason || `${u.degree}${u.specialty ? ` · ${u.specialty}` : ""}`}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => follow(u.id)}
                    disabled={followPendingIds.has(u.id)} data-testid="follow-scholar-button"
                    className="text-[color:var(--ayur-terracotta)] hover:bg-[color:var(--ayur-cream-2)] text-xs">
                    {followPendingIds.has(u.id) ? "…" : "Follow"}
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="paper-card p-5">
            <div className="label-eyebrow mb-3">Upcoming meet-ups</div>
            {events.length === 0 && <div className="text-sm text-[color:var(--ayur-mute)]">No meet-ups yet. <Link to="/app/meetups" className="text-[color:var(--ayur-terracotta)]">Host one →</Link></div>}
            <div className="space-y-3">
              {events.map((e) => (
                <Link to="/app/meetups" key={e.id} className="block">
                  <div className="text-sm font-semibold">{e.title}</div>
                  <div className="text-xs text-[color:var(--ayur-mute)]">{e.location} · {new Date(e.date).toLocaleDateString()}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
